import { FilterBar } from "@/components/FilterBar";
import { EventHeader } from "@/components/eventHeader";
import { SearchIcon } from "@/components/icons";
import { MainContainer } from "@/components/mainContainer";
import * as Inputs from "../../../components/inputs";
import * as Fonts from "../../../components/texts";
import * as T from "../../../components/table";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { Pagination } from "@/components/pagination";
import { MenuActionButton } from "@/components/menuActionButton";
import { MenuPopUp } from "@/components/menuPopUp";
import { UserCheckMark } from "@/components/icons/userCheckMark";
import { UserClose } from "@/components/icons/userClose";
import { MultipleUsers } from "@/components/icons/multipleUsers";
import { useEvent } from "@/hooks/useEvent";
import {
  EventByIdDto,
  EventClientIndexDto,
  EventClientIndexFilterDto,
} from "../types";
import { useParams } from "react-router-dom";
import { useEventClient } from "@/hooks/useEventClient";
import { useCustomToast } from "@/hooks/useToast";
import LoadingOverlay from "@/components/overlay";
const initialFilter: EventClientIndexFilterDto = {
  name: "",
  cpf: "",
  email: "",
};
export const EntranceReport = () => {
  const [filter, setFilter] =
    useState<EventClientIndexFilterDto>(initialFilter);
  const [page, setPage] = useState<number>(1);
  const [lastPage] = useState<number>(1);
  const { id: eventId } = useParams<{ id: string }>();
  const { getEventById } = useEvent();
  const {
    getEventClients,
    confirmGuestPresenceManual,
    sendInviteMail,
    downloadList,
  } = useEventClient();
  const [event, setEvent] = useState<EventByIdDto>();
  const [clients, setClients] = useState<EventClientIndexDto[]>([]);
  const [showMenuPopUp, setShowMenuPopUp] = useState<undefined | number>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stopedTyping, setStopedTyping] = useState(false);
  const { customToast } = useCustomToast();
  const [filterType, setFilterType] = useState("");

  const callBackClients = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      getEventClients({
        page,
        perPage: 999999,
        eventId: Number(eventId),
        ...filter,
      }),
      getEventById(Number(eventId)),
    ])
      .then(([clientData, eventData]) => {
        setClients(clientData.data.data);
        setEvent(eventData.data);
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [page, filter]);

  useEffect(() => {
    callBackClients();
  }, [callBackClients]);
  useEffect(() => {
    if (stopedTyping) {
      if (page === 1) {
        callBackClients();
      } else {
        setPage(1);
      }
      setStopedTyping(false);
    }
  }, [filter, stopedTyping]);

  const handleSendEmail = (id: number) => {
    setIsLoading(true);
    sendInviteMail(id)
      .then(() => {
        customToast("success", "2º Via enviada.");
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callBackClients();
        setIsLoading(false);
      });
  };

  const handleExportList = (eventId: number) => {
    downloadList(eventId)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `convidados-${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      });
  };

  //Tabela de convidados
  const guestsTableData = clients.flatMap((client) =>
    (client.guests ?? []).map((guest) => ({
      id: guest.id,
      guestName: guest.name,
      clientName: client.name,
      status: guest.isPresent ? "Presente" : "Ausente",
      checkin: guest.checkin,
      validationMethod: guest.validationMethod,
      validatedBy: guest.validatedBy,
    }))
  );

  const totalAusente =
    (event?.totalInvitation || 0) - (event?.totalPresentGuests || 0);

  const filters = [
    {
      field: (
        <HStack w="30%" justifyContent="flex-end" key={1}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Buscar por:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const selected = event.target.value;
              setFilterType(selected);
            }}
          >
            <option value="">Selecione</option>
            <option value="guestName">Nome do Convidado</option>
            <option value="name">Cliente</option>
            <option value="checkin">Check-in</option>
            <option value="validationMethod">Modo de Validação</option>
            <option value="validatedBy">Realizado Por</option>
          </Select>
        </HStack>
      ),
    },
    {
      field: (
        <InputGroup alignItems="center" key={2}>
          <InputLeftElement pointerEvents="none" top="" left={1}>
            <SearchIcon />
          </InputLeftElement>
          <Inputs.SimpleText
            placeholder="O que você procura?"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(event) => {
              const value = event.target.value;
              setFilter({ ...filter, [filterType]: value });
            }}
          />
        </InputGroup>
      ),
    },
    {
      field: (
        <HStack w="30%" justifyContent="flex-start" key={3}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Status:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const value = event.target.value;

              let isPresent: boolean | undefined;

              if (value === "present") {
                isPresent = true;
              } else if (value === "absent") {
                isPresent = false;
              } else {
                isPresent = undefined;
              }

              setFilter({ ...filter, isPresent });
            }}
          >
            <option value="">Selecione</option>
            <option value="">Todos</option>
            <option value="present">Presente</option>
            <option value="absent">Ausente</option>
          </Select>
        </HStack>
      ),
    },
  ];

  return (
    <>
      <EventHeader eventName={event?.name} />
      <MainContainer
        height={isLoading || clients.length < 1 ? undefined : "100%"}
      >
        {isLoading && <LoadingOverlay />}
        <FilterBar title={"Relatório de Acesso"} filters={filters} />
        <Stack p={9} mb="0 !important" w="100%">
          <Box bgColor="neutral.100" borderRadius={5} p={3}>
            <HStack w="100%" spacing={5} mt={5}>
              <Stack w="75%">
                <Text
                  fontFamily="Inter"
                  fontStyle="normal"
                  fontWeight="600"
                  fontSize="24px"
                  lineHeight="31px"
                  color="neutral.600"
                >
                  Informações do Evento
                </Text>
              </Stack>
            </HStack>
            <Divider h={2} />
            <Stack p={2}>
              <Stack key={event?.id}>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Evento</Text>
                    <Text>{event?.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Tipo do Evento</Text>
                    <Text>{event?.type.name}</Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Unidade</Text>
                    <Text>{event?.branch.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Data e Hora</Text>
                    <Text>
                      {event?.date
                        ? new Date(event.date).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Tipo do pacote</Text>
                    <Text>{event?.team.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Local</Text>
                    <Text>{event?.local}</Text>
                  </HStack>
                </HStack>
              </Stack>
            </Stack>
          </Box>
          <Stack w="100%">
            <Box mt={5}>
              <Text fontWeight="bold" fontSize="30px">
                Dashboard
              </Text>
              <Text fontWeight="semibold" fontSize="15px">
                Dados registrados sobre o evento.
              </Text>
            </Box>
            <Box bgColor="neutral.10" borderRadius={5} p={3}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} p={4}>
                <Box borderRadius="md" maxW="80%">
                  <Box
                    color="white"
                    p={5}
                    borderRadius="md"
                    bgGradient="linear(to-r, #00FF00 0%, black 140%)"
                    boxShadow="lg"
                  >
                    <Flex align="center" gap={3}>
                      <Icon boxSize={10} mr={5}>
                        <UserCheckMark />
                      </Icon>
                      <Stat>
                        <StatNumber fontSize="2xl">
                          {event?.totalPresentGuests}
                        </StatNumber>
                        <StatLabel fontSize="20px">
                          Total de Presentes
                        </StatLabel>
                      </Stat>
                    </Flex>
                  </Box>
                  <SimpleGrid columns={2} spacing={2} mt={3}>
                    {event?.totalPerEntrance?.map((present) => (
                      <Box
                        bg="white"
                        p={3}
                        border="1px solid #ddd"
                        borderRadius="md"
                        textAlign="center"
                        boxShadow="lg"
                        key={present.entranceId}
                      >
                        <Text fontSize="sm">{present.entranceName}</Text>
                        <Text fontSize="lg" fontWeight="bold">
                          {present.present}
                        </Text>
                        <Text fontSize="sm">Total de Convidados</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box borderRadius="md" maxW="80%">
                  <Box
                    color="white"
                    p={5}
                    borderRadius="md"
                    bgGradient="linear(to-r, #FF0000 0%, black 140%)"
                    boxShadow="lg"
                  >
                    <Flex align="center" gap={3}>
                      <Icon boxSize={10} mr={5}>
                        <UserClose />
                      </Icon>
                      <Stat>
                        <StatNumber fontSize="2xl">{totalAusente}</StatNumber>
                        <StatLabel fontSize="20px">Total de Ausentes</StatLabel>
                      </Stat>
                    </Flex>
                  </Box>
                  <SimpleGrid columns={2} spacing={2} mt={3}>
                    {event?.totalPerEntrance?.map((absent) => (
                      <Box
                        bg="white"
                        p={3}
                        border="1px solid #ddd"
                        borderRadius="md"
                        textAlign="center"
                        boxShadow="lg"
                        key={absent.entranceId}
                      >
                        <Text fontSize="sm">{absent.entranceName}</Text>
                        <Text fontSize="lg" fontWeight="bold">
                          {absent.absent}
                        </Text>
                        <Text fontSize="sm">Total de Convidados</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box borderRadius="md" maxW="80%">
                  <Box
                    color="white"
                    p={5}
                    borderRadius="md"
                    bgGradient="linear(to-r, #1E90FF 0%, black 150%)"
                    boxShadow="lg"
                  >
                    <Flex align="center" gap={3}>
                      <Icon boxSize={10} mr={5}>
                        <MultipleUsers />
                      </Icon>
                      <Stat>
                        <StatNumber fontSize="2xl">
                          {event?.totalInvitation}
                        </StatNumber>
                        <StatLabel fontSize="20px">
                          Total de Convidados
                        </StatLabel>
                      </Stat>
                    </Flex>
                  </Box>
                  <SimpleGrid columns={2} spacing={2} mt={3}>
                    {event?.totalPerEntrance?.map((total) => (
                      <Box
                        bg="white"
                        p={3}
                        border="1px solid #ddd"
                        borderRadius="md"
                        textAlign="center"
                        boxShadow="lg"
                        key={total.entranceId}
                      >
                        <Text fontSize="sm">{total.entranceName}</Text>
                        <Text fontSize="lg" fontWeight="bold">
                          {total.total}
                        </Text>
                        <Text fontSize="sm">Total de Convidados</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              </SimpleGrid>
            </Box>
          </Stack>
          <Stack w="100%">
            <HStack justifyContent="end" mt={5}>
              <Button
                textDecoration="underline"
                variant="ghost"
                color="blue"
                fontSize="15px"
                _hover={{ color: "neutral.50" }}
                onClick={() => handleExportList(Number(eventId))}
              >
                exportar listagem
              </Button>
            </HStack>
            <Stack w="100%">
              <TableContainer w="100%">
                <Table>
                  <Thead bgColor="neutral.300">
                    <Tr>
                      <Th w="20%">
                        <Fonts.TableTheadText>
                          Nome do Convidado
                        </Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>Cliente</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>Check-In</Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>
                          Modo de Validação
                        </Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>
                          Realizado Por
                        </Fonts.TableTheadText>
                      </Th>
                      <Th textAlign="center">
                        <Fonts.TableTheadText>Status</Fonts.TableTheadText>
                      </Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {guestsTableData.map((grad, idx) => (
                      <T.Body key={idx}>
                        <Td w="20%">
                          <Fonts.TableBodyText>
                            {grad.guestName}
                          </Fonts.TableBodyText>
                        </Td>
                        <Td textAlign="center">
                          <Fonts.TableBodyText>
                            {grad.clientName}
                          </Fonts.TableBodyText>
                        </Td>
                        <Td textAlign="center">
                          <Fonts.TableBodyText>
                            {grad.checkin
                              ? new Date(grad.checkin).toLocaleString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "Ainda não Realizado"}
                          </Fonts.TableBodyText>
                        </Td>
                        <Td textAlign="center">
                          <Fonts.TableBodyText>
                            {grad.validationMethod
                              ? grad.validationMethod
                              : "Ainda não Realizado"}
                          </Fonts.TableBodyText>
                        </Td>
                        <Td textAlign="center">
                          <Fonts.TableBodyText>
                            {grad.validatedBy
                              ? grad.validatedBy
                              : "Ainda não Realizado"}
                          </Fonts.TableBodyText>
                        </Td>
                        <Td textAlign="center">
                          <Badge
                            pl={3}
                            mr={3}
                            w={5}
                            h={5}
                            borderRadius="50%"
                            style={{
                              backgroundColor:
                                grad.status === "Presente" ? "green" : "red",
                            }}
                          />
                          <Fonts.TableBodyText>
                            {grad.status}
                          </Fonts.TableBodyText>
                        </Td>
                        <Td>
                          <MenuActionButton
                            w="10%"
                            color="#2F2F2F"
                            onClick={() => setShowMenuPopUp(grad.id)}
                            _hover={{
                              bgColor: "neutral.200",
                            }}
                            _active={{
                              bgColor: "neutral.300",
                            }}
                          />
                        </Td>
                        {showMenuPopUp === grad.id && (
                          <MenuPopUp
                            top="-4px"
                            right="35px"
                            options={[
                              {
                                title: "Enviar 2º Via",
                                onClick: () => handleSendEmail(grad.id),
                              },
                              {
                                title: "Confirmar Entrada",
                                onClick: () => {
                                  setIsLoading(true);
                                  confirmGuestPresenceManual(grad.id, {
                                    isPresent: true,
                                  })
                                    .then(() => {
                                      customToast(
                                        "success",
                                        "Presença confirmada com sucesso!"
                                      );
                                    })
                                    .catch((error) => {
                                      customToast(
                                        "error",
                                        error.response?.data?.message
                                      );
                                    })
                                    .finally(() => {
                                      callBackClients();
                                      setIsLoading(false);
                                    });
                                },
                              },
                            ]}
                            setShowMenuPopUp={setShowMenuPopUp}
                          />
                        )}
                      </T.Body>
                    ))}
                  </Tbody>
                  <Tfoot w="100%"></Tfoot>
                </Table>
                <Pagination page={page} lastPage={lastPage} onClick={setPage} />
              </TableContainer>
            </Stack>
          </Stack>
        </Stack>
      </MainContainer>
    </>
  );
};
